import { Injectable, NgZone } from '@angular/core';
import { IUserStreamEvent } from '../interfaces/user-stream.interface';
import {
  toUserStreamEnvelope,
  toUserStreamReset
} from '../utils/user-stream.parser';
import { Observable, Subscriber } from 'rxjs';
import { JwtService } from '../../../core/services/auth/jwt.service';
import { IActiveExecutionRaw } from '../interfaces/active-execution.interface';
import { IHITLResponse } from '../interfaces/hitl.interface';
import { IStreamMessageRequestRaw } from '../interfaces/stream-message-request.interface';
import { EnumFrameOutcome } from '../enums/frame-outcome.enum';
import { IAyaEnvelope } from '../interfaces/aya-stream.interface';
import { AyaStreamReducer } from '../utils/aya-stream.reducer';
import { ISSEFrame, SseParser } from '../utils/sse-parser';

export type ISSERequestBody = IStreamMessageRequestRaw | IHITLResponse;

export const RUN_ID_HEADER = 'X-Aya-Run-Id';

export interface IAyaStreamError {
  status: number;
  code?: string;
  message: string;
  execution?: IActiveExecutionRaw | null;
}

export interface IAyaStreamEvent {
  envelope: IAyaEnvelope;
  cursor: string | null;
  terminal: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ServerSentEventsService {
  private get streamHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: this.jwtService.accessTokenWithBearer
    };
  }

  constructor(
    private zone: NgZone,
    private jwtService: JwtService
  ) {}

  /**
   * Streaming v2: o `run_id` só é conhecido quando os cabeçalhos chegam, então o
   * reducer nasce ali e valida identidade e ordem na borda do transporte.
   */
  connectStream(
    url: string,
    sessionId: string,
    body: ISSERequestBody | null,
    cursor?: string | null,
    knownRunId?: string
  ): Observable<IAyaStreamEvent> {
    return new Observable<IAyaStreamEvent>((observer) => {
      const abortController = new AbortController();
      this.processAyaStream(url, sessionId, body, abortController.signal, observer, cursor, knownRunId);
      return () => abortController.abort();
    });
  }

  /**
   * Canal de escopo do usuário: mesmo transporte do stream de run, sem a
   * validação de `session_id`/`run_id`. O cursor é opaco e vem do servidor.
   */
  connectUserStream(
    url: string,
    cursor?: string | null
  ): Observable<IUserStreamEvent> {
    return new Observable<IUserStreamEvent>((observer) => {
      const abortController = new AbortController();
      this.processUserStream(url, abortController.signal, observer, cursor);
      return () => abortController.abort();
    });
  }

  private async processUserStream(
    url: string,
    signal: AbortSignal,
    observer: Subscriber<IUserStreamEvent>,
    cursor?: string | null
  ): Promise<void> {
    const response = await this.fetchStream(url, null, signal, cursor);
    const reader = response?.ok ? response.body?.getReader() : null;
    if (!reader) {
      return this.failUserStream(observer, response ?? null);
    }

    let atual = cursor ?? null;
    // O canal fica silencioso até algo mudar: sem avisar a abertura, quem
    // consome acharia que ele está fora do ar e ligaria o polling.
    this.zone.run(() => observer.next({ envelope: null, cursor: atual }));

    try {
      for await (const frame of this.streamFrames(reader)) {
        const reset = toUserStreamReset(frame.data);
        if (reset) {
          this.zone.run(() => observer.error({ status: 410, reset }));
          return;
        }
        const envelope = toUserStreamEnvelope(frame.data);
        if (!envelope) {
          continue;
        }
        // O cursor avança mesmo quando o efeito é deduplicado adiante.
        atual = frame.id ?? atual;
        this.zone.run(() => observer.next({ envelope, cursor: atual }));
      }
      this.zone.run(() => observer.complete());
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        this.zone.run(() => observer.error(error));
      }
    }
  }

  private async failUserStream(
    observer: Subscriber<IUserStreamEvent>,
    response: Response | null
  ): Promise<void> {
    const error = await this.toStreamError(response);
    this.zone.run(() => observer.error(error));
  }

  private async fetchStream(
    url: string,
    body: ISSERequestBody | null,
    signal: AbortSignal,
    cursor?: string | null
  ): Promise<Response | null> {
    const headers: Record<string, string> = { ...this.streamHeaders };
    if (cursor) {
      headers['Last-Event-ID'] = cursor;
    }

    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers,
      ...(body && { body: JSON.stringify(body) }),
      signal
    }).catch((error) => {
      if (error.name !== 'AbortError') {
        console.error('Fetch error:', error);
      }
      return null;
    });
  }

  private async processAyaStream(
    url: string,
    sessionId: string,
    body: ISSERequestBody | null,
    signal: AbortSignal,
    observer: Subscriber<IAyaStreamEvent>,
    cursor?: string | null,
    knownRunId?: string
  ): Promise<void> {
    const response = await this.fetchStream(url, body, signal, cursor);

    if (!response?.ok) {
      return this.failAyaStream(observer, response);
    }

    const runId = knownRunId ?? response.headers?.get(RUN_ID_HEADER);
    const reader = response.body?.getReader();
    if (!runId || !reader) {
      this.zone.run(() => observer.error({ status: response.status, message: 'Stream sem run id ou corpo de resposta.' }));
      return;
    }

    const reducer = new AyaStreamReducer(sessionId, runId);

    try {
      for await (const frame of this.streamFrames(reader)) {
        const result = reducer.apply(frame);
        if (result.outcome !== EnumFrameOutcome.APPLIED || !result.envelope) {
          continue;
        }

        const event: IAyaStreamEvent = {
          envelope: result.envelope,
          cursor: reducer.cursor,
          terminal: result.terminal
        };
        this.zone.run(() => observer.next(event));

        if (result.terminal) {
          this.zone.run(() => observer.complete());
          return;
        }
      }

      // EOF sem terminal não confirma sucesso: quem consome reconcilia.
      this.zone.run(() => observer.complete());
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        this.zone.run(() => observer.error(error));
      }
    }
  }

  private async failAyaStream(
    observer: Subscriber<IAyaStreamEvent>,
    response: Response | null
  ): Promise<void> {
    const error = await this.toStreamError(response);
    this.zone.run(() => observer.error(error));
  }

  /** Preserva `execution` do 409: sem ela não há como reconciliar a geração ativa. */
  private async toStreamError(response: Response | null): Promise<IAyaStreamError> {
    const status = response?.status ?? 0;
    const fallback = `HTTP error! status: ${status}`;

    if (!response) {
      return { status, message: fallback };
    }

    try {
      const parsed = await response.json();
      const envelope = parsed?.error;
      if (envelope?.code) {
        return {
          status,
          code: envelope.code,
          message: envelope.message ?? fallback,
          execution: envelope.execution ?? null
        };
      }
    } catch {
    }

    return { status, message: fallback };
  }

  private async *streamFrames(
    reader: ReadableStreamDefaultReader<Uint8Array>
  ): AsyncGenerator<ISSEFrame> {
    const parser = new SseParser();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        for (const frame of parser.push(value)) {
          yield frame;
        }
      }
    } finally {
      parser.finish();
      reader.releaseLock();
    }
  }

}
